import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminLead, adminUpdateLead, adminAddActivity } from "../../lib/api";

const STATUSES = ["New", "Contacted", "Quoted", "Confirmed", "Flown", "Lost"];
const PAY = ["Unpaid", "Advance received", "Paid in full", "Refunded"];
const INR = (n) => "\u20B9" + (Number(n) || 0).toLocaleString("en-IN");

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [saved, setSaved] = useState("");
  const [note, setNote] = useState("");

  const load = () => adminLead(id).then(setLead).catch(() => setLead(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (lead === false) return <p className="adm-sub">Lead not found. <Link to="/admin/leads">Back to leads</Link></p>;
  if (!lead) return <p className="adm-sub">Loading…</p>;

  const c = lead.computed || {};
  const patch = async (body) => { const r = await adminUpdateLead(id, body); setLead({ ...lead, ...r, computed: c }); setSaved("Saved"); setTimeout(() => setSaved(""), 1500); };
  const commission = (Number(lead.selling_price) || 0) - (Number(lead.operator_price) || 0);
  const addNote = async () => { if (!note.trim()) return; const r = await adminAddActivity(id, note.trim()); setLead({ ...lead, activity: r.activity }); setNote(""); };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div>
      <p style={{ margin: "0 0 8px" }}><Link className="adm-link" to="/admin/leads">{"\u2190"} All leads</Link></p>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 className="adm-h1" style={{ margin: 0 }}>{lead.reference}</h1>
        <span className={"pill " + lead.status}>{lead.status}</span>
        {saved ? <span style={{ color: "var(--ok)", fontWeight: 700 }}>{saved}</span> : null}
      </div>
      <p className="adm-sub">Received {fmt(lead.created_at)}</p>

      <div className="adm-grid2">
        <div>
          <div className="panel">
            <h3>Trip & customer</h3>
            <dl className="kv">
              <dt>Name</dt><dd>{lead.name}</dd>
              <dt>Phone</dt><dd><a className="adm-link" href={`tel:${lead.phone}`}>{lead.phone}</a></dd>
              <dt>Email</dt><dd>{lead.email || "\u2014"}</dd>
              <dt>Route</dt><dd>{lead.from_city} {"\u2192"} {lead.to_city}</dd>
              <dt>Date</dt><dd>{lead.date || "\u2014"}{lead.return_date ? ` (return ${lead.return_date})` : ""}</dd>
              <dt>Time</dt><dd>{lead.time_pref}</dd>
              <dt>Passengers</dt><dd>{lead.passengers}</dd>
              <dt>Preference</dt><dd>{lead.aircraft_pref || "No preference"}</dd>
              <dt>Distance</dt><dd>{c.distance_nm ? `${c.distance_nm.toLocaleString("en-IN")} nm · ${c.distance_km.toLocaleString("en-IN")} km` : "\u2014"}</dd>
              <dt>Notes</dt><dd>{lead.notes || "\u2014"}</dd>
            </dl>
          </div>

          <div className="panel">
            <h3>Suitable aircraft ({(c.results || []).length})</h3>
            {(c.results || []).map((r) => (
              <div key={r.code} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--line)", fontSize: 14 }}>
                <span>{r.name} {r.badge ? <span className="pill Quoted" style={{ marginLeft: 6 }}>{r.badge}</span> : null}</span>
                <span style={{ color: "var(--muted)" }}>{r.flight_time} · {r.stops ? `${r.stops} stop` : "nonstop"}</span>
              </div>
            ))}
          </div>

          <div className="panel">
            <h3>Matching operators ({(c.matching_operators || []).length})</h3>
            <p className="adm-sub" style={{ marginTop: -6 }}>Private — never shown on the public website.</p>
            {(c.matching_operators || []).map((o) => (
              <div className="op-card" key={o.id}>
                <div className="name">{o.name}</div>
                <div className="meta">{o.city} · {o.phone} {o.emails?.length ? "· " + o.emails[0] : ""}</div>
                <div className="meta" style={{ marginTop: 4 }}>{o.aircraft.map((a) => `${a.name} (${a.reg}, ${a.seats} seats)`).join(" · ")}</div>
              </div>
            ))}
            {!(c.matching_operators || []).length ? <p className="adm-sub">No operators matched this route/pax in the current fleet.</p> : null}
          </div>
        </div>

        <div>
          <div className="panel">
            <h3>Manage</h3>
            <div className="field"><label>Status</label>
              <select value={lead.status} onChange={(e) => patch({ status: e.target.value })}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div className="field"><label>Operator chosen</label>
              <input defaultValue={lead.operator_chosen} onBlur={(e) => e.target.value !== lead.operator_chosen && patch({ operator_chosen: e.target.value })} placeholder="Operator name" />
            </div>
            <div className="field"><label>Operator price (₹)</label>
              <input type="number" defaultValue={lead.operator_price ?? ""} onBlur={(e) => patch({ operator_price: Number(e.target.value) || 0 })} />
            </div>
            <div className="field"><label>Your selling price (₹)</label>
              <input type="number" defaultValue={lead.selling_price ?? ""} onBlur={(e) => patch({ selling_price: Number(e.target.value) || 0 })} />
            </div>
            <div className="kpi" style={{ margin: "4px 0 12px" }}><div className="v">{INR(commission)}</div><div className="l">Your commission</div></div>
            <div className="field"><label>Payment status</label>
              <select value={lead.payment_status} onChange={(e) => patch({ payment_status: e.target.value })}>{PAY.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div className="field"><label>Internal notes</label>
              <textarea defaultValue={lead.internal_notes} onBlur={(e) => e.target.value !== lead.internal_notes && patch({ internal_notes: e.target.value })} placeholder="Private notes about this lead" />
            </div>
          </div>

          <div className="panel">
            <h3>Activity</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input className="field" style={{ flex: 1, padding: "10px 12px", border: "1px solid var(--line2)", borderRadius: 10 }} placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <button className="adm-btn primary" type="button" onClick={addNote}>Add</button>
            </div>
            <ul className="timeline">
              {[...(lead.activity || [])].reverse().map((a, i) => (<li key={i}><div>{a.text}</div><div className="at">{fmt(a.at)}</div></li>))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
